import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Loader2, FolderOpen, ExternalLink, Copy, Sparkles, FileText } from 'lucide-react';

export default function Categories() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', longer_title: '', subtitle: '', short_description: '', long_description: '', status: 'active' });

  const load = async () => {
    try {
      const [catRes, pageRes] = await Promise.all([
        api.get('/categories'),
        api.get('/pages'),
      ]);
      setCategories(catRes.data);
      setPages(pageRes.data);
    } catch { toast.error('Failed to load'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', longer_title: '', subtitle: '', short_description: '', long_description: '', status: 'active' });
    setDialogOpen(true);
  };

  const openEdit = (cat) => {
    setEditing(cat);
    setForm({ name: cat.name, longer_title: cat.longer_title || '', subtitle: cat.subtitle || '', short_description: cat.short_description || '', long_description: cat.long_description || '', status: cat.status || 'active' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    try {
      if (editing) {
        await api.put(`/categories/${editing.id}`, form);
        toast.success('Category updated');
      } else {
        await api.post('/categories', form);
        toast.success('Category created');
      }
      setDialogOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this category? Products will become uncategorized.')) return;
    try {
      await api.delete(`/categories/${id}`);
      toast.success('Deleted');
      load();
    } catch { toast.error('Delete failed'); }
  };

  const createLandingPage = async (cat) => {
    try {
      toast.info('Creating landing page...');
      const page = {
        product_id: '',
        page_type: 'category_page',
        title: `${cat.name} - Landing Page`,
        slug: `cat-${cat.slug}`,
        status: 'draft',
        category_id: cat.id,
      };
      const { data } = await api.post('/pages', page);
      toast.success('Landing page created');
      navigate(`/admin/pages/${data.id}`);
    } catch { toast.error('Failed to create page'); }
  };

  const getCategoryPages = (catId) => pages.filter(p => p.category_id === catId);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-[#1A9DA5]" /></div>;

  return (
    <div className="space-y-6" data-testid="categories-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-['Outfit']">Categories</h1>
          <p className="text-sm text-muted-foreground">{categories.length} categories</p>
        </div>
        <Button onClick={openCreate} className="bg-[#1A9DA5] hover:bg-[#158D92]" data-testid="create-category-btn">
          <Plus className="w-4 h-4 mr-1" />Add Category
        </Button>
      </div>

      {categories.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-muted-foreground">
          <FolderOpen className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p>No categories yet. Create one to organize your products.</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {categories.map(cat => {
            const catPages = getCategoryPages(cat.id);
            return (
              <Card key={cat.id} data-testid={`category-card-${cat.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-base font-['Outfit']">{cat.name}</h3>
                        <Badge variant="outline" className={cat.status === 'active' ? 'bg-emerald-500/10 text-emerald-600' : ''}>{cat.status}</Badge>
                      </div>
                      {cat.longer_title && <p className="text-sm text-muted-foreground">{cat.longer_title}</p>}
                      {cat.short_description && <p className="text-xs text-muted-foreground mt-1">{cat.short_description}</p>}
                      <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                        <span className="font-mono bg-muted px-2 py-0.5 rounded">/{cat.slug}</span>
                        {catPages.length > 0 && <Badge variant="outline" className="text-[10px]">{catPages.length} landing page(s)</Badge>}
                      </div>
                      {catPages.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {catPages.map(p => (
                            <div key={p.id} className="flex items-center gap-2 text-xs">
                              <Badge variant="outline" className={`text-[10px] h-4 ${p.status === 'published' ? 'bg-emerald-500/10 text-emerald-600' : ''}`}>{p.status}</Badge>
                              <span className="truncate">{p.title}</span>
                              <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/l/${p.slug}`); toast.success('Link copied'); }}><Copy className="w-2.5 h-2.5" /></Button>
                              <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => navigate(`/admin/pages/${p.id}`)}><Pencil className="w-2.5 h-2.5" /></Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => createLandingPage(cat)} title="Create landing page">
                        <FileText className="w-3 h-3 mr-1" />Landing Page
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => window.open(`/category/${cat.slug}`, '_blank')} title="Preview"><ExternalLink className="w-3.5 h-3.5" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(cat)}><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(cat.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setEditing(null); }}>
        <DialogContent className="max-w-lg" data-testid="category-dialog">
          <DialogHeader><DialogTitle>{editing ? 'Edit Category' : 'Create Category'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-sm font-medium">Name</label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g., Marketing" data-testid="category-name-input" /></div>
            <div><label className="text-sm font-medium">Longer Title</label><Input value={form.longer_title} onChange={e => setForm(f => ({ ...f, longer_title: e.target.value }))} placeholder="e.g., Marketing & Growth Strategies" /></div>
            <div><label className="text-sm font-medium">Subtitle</label><Input value={form.subtitle} onChange={e => setForm(f => ({ ...f, subtitle: e.target.value }))} placeholder="e.g., Grow your audience and revenue" /></div>
            <div><label className="text-sm font-medium">Short Description</label><Textarea value={form.short_description} onChange={e => setForm(f => ({ ...f, short_description: e.target.value }))} rows={2} placeholder="Brief description for category cards" /></div>
            <div><label className="text-sm font-medium">Long Description</label><Textarea value={form.long_description} onChange={e => setForm(f => ({ ...f, long_description: e.target.value }))} rows={4} placeholder="Detailed description for the category page" /></div>
            <div><label className="text-sm font-medium">Status</label>
              <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleSave} className="w-full bg-[#1A9DA5] hover:bg-[#158D92]" data-testid="save-category-btn">
              {editing ? 'Update Category' : 'Create Category'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
